import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';

const CBCustomTimePicker = ({
  value, // Expected format: "HH:mm"
  onChange,
  label,
  className = '',
  inputClassName = '',
  disabled = false,
  required = false,
  name,
  id,
  // step prop is handled internally now
  min, // Optional: min time "HH:mm"
  max, // Optional: max time "HH:mm"
  militaryTime = false, // Optional: use 24-hour format display
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState(null);
  const [selectedMinute, setSelectedMinute] = useState(null);
  const pickerRef = useRef(null);

  const formatTime = (hour, minute) => {
    if (hour === null || minute === null) return '';
    const time = moment({ hour, minute });
    return militaryTime ? time.format('HH:mm') : time.format('hh:mm A');
  };

  const parseValue = (val) => {
    const time = moment(val, 'HH:mm');
    if (time.isValid()) {
      return { hour: time.hour(), minute: time.minute() };
    }
    return { hour: null, minute: null };
  };

  useEffect(() => {
    const { hour, minute } = parseValue(value);
    setSelectedHour(hour);
    setSelectedMinute(minute);
  }, [value]);

  // Close picker if clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [pickerRef]);


  const handleOpen = () => {
    if (!disabled) {
      const { hour, minute } = parseValue(value);
      setSelectedHour(hour ?? moment().hour()); // Default to current hour if no value
      setSelectedMinute(minute ?? 0); // Default to 00 if no value
      setIsOpen(true);
    }
  };

  const handleOk = () => {
    if (selectedHour !== null && selectedMinute !== null) {
      const newTimeString = moment({ hour: selectedHour, minute: selectedMinute }).format('HH:mm');
      if (onChange) {
        onChange(newTimeString);
      }
    }
    setIsOpen(false);
  };

  const handleCancel = () => {
     // Reset to original value on cancel
     const { hour, minute } = parseValue(value);
     setSelectedHour(hour);
     setSelectedMinute(minute);
     setIsOpen(false);
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = [0, 15, 30, 45];

  const displayValue = formatTime(parseValue(value).hour, parseValue(value).minute);

  // Basic styling - can be enhanced significantly
  const pickerStyles = {
    position: 'absolute',
    backgroundColor: 'white',
    border: '1px solid #ccc',
    zIndex: 1000,
    padding: '10px',
    marginTop: '2px',
    boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  };

  const timeColumnStyles = {
      maxHeight: '150px',
      overflowY: 'auto',
      border: '1px solid #eee',
      padding: '5px',
  };

  const timeItemStyles = (isSelected) => ({
      padding: '2px 5px',
      cursor: 'pointer',
      backgroundColor: isSelected ? '#e0e0e0' : 'transparent',
      borderRadius: '3px',
  });

  return (
    <div className={`relative ${className}`} ref={pickerRef}>
      {label && <label htmlFor={id || name} className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <input
        type="text" // Use text input to display formatted time and trigger custom picker
        id={id || name}
        name={name}
        value={displayValue}
        onClick={handleOpen}
        readOnly // Prevent manual typing, force picker usage
        disabled={disabled}
        required={required}
        placeholder={militaryTime ? "HH:mm" : "hh:mm A"}
        className={`block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${inputClassName} ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white cursor-pointer'}`}
        {...props}
      />
      {isOpen && (
        <div style={pickerStyles}>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
             {/* Hour Selector */}
             <div style={timeColumnStyles}>
                {hours.map(hour => (
                    <div
                        key={hour}
                        style={timeItemStyles(hour === selectedHour)}
                        onClick={() => setSelectedHour(hour)}
                    >
                        {moment({ hour }).format(militaryTime ? 'HH' : 'hh A')}
                    </div>
                ))}
             </div>
             {/* Minute Selector */}
             <div style={timeColumnStyles}>
                {minutes.map(minute => (
                    <div
                        key={minute}
                        style={timeItemStyles(minute === selectedMinute)}
                        onClick={() => setSelectedMinute(minute)}
                    >
                        {moment({ minute }).format('mm')}
                    </div>
                ))}
             </div>
          </div>
          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '5px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
            <button
              type="button"
              onClick={handleCancel}
              className="btn-ternary"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleOk}
              className="btn-primary"
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export { CBCustomTimePicker };